import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { MusicaRaioX } from '@/shared/types';
import { montarCartoesRaioX } from '../../domain/usecases/montarCartoesRaioX';
import StoriesProgressBar from './StoriesProgressBar';

/** Tempo de cada cartão de texto antes de girar para o próximo. */
const CARTAO_MS = 6_000;

const TEXT_SHADOW = '0 1px 8px rgba(0,0,0,0.95), 0 0 3px rgba(0,0,0,1)';

interface Props {
  dados:   MusicaRaioX;
  visivel: boolean;
}

/**
 * Faixa inferior da TV com o raio-x da música tocando.
 *
 * Ocupa a largura da coluna de vídeo — com a AdsSidebar no ar ela para rente
 * à sidebar e fecha o "L" sem nenhum cálculo de largura.
 *
 * O motor de rotação é um rAF próprio, e não o `useAdsTimer`: aquele hook é
 * acoplado a `Midia[]`, `blobMap` e refs de <img>/<video>.
 */
export default function TvRaioXBar({ dados, visivel }: Props) {
  const cartoes = useMemo(() => montarCartoesRaioX(dados), [dados]);

  const [idx, setIdx]           = useState(0);
  const [progress, setProgress] = useState(0);
  const [capaOk, setCapaOk]     = useState(true);

  // Novo raio-x: volta pro primeiro cartão
  useEffect(() => {
    setIdx(0);
    setProgress(0);
    setCapaOk(true);
  }, [dados.youtube_video_id]);

  // Rotação por requestAnimationFrame — tempo real, não contagem de frames,
  // para não derrapar quando a TV Box perde quadros.
  // `youtube_video_id` entra nas dependências para que a troca de música
  // sempre reinicie `inicio`, mesmo quando a contagem de cartões não muda.
  useEffect(() => {
    if (!visivel || cartoes.length <= 1) return;

    let raf    = 0;
    let inicio = performance.now();

    const tick = (agora: number) => {
      const p = Math.min((agora - inicio) / CARTAO_MS, 1);
      setProgress(p);
      if (p >= 1) {
        inicio = agora;
        setIdx(i => (i + 1) % cartoes.length);
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visivel, cartoes.length, dados.youtube_video_id]);

  const cartao = cartoes[idx] ?? null;
  const mostrar = visivel && cartoes.length > 0;

  return (
    <AnimatePresence>
      {mostrar && cartao && (
        <motion.div
          className="absolute bottom-0 left-0 right-0 z-[9] pointer-events-none overflow-hidden"
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0,  opacity: 1 }}
          exit={{    y: 24, opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          aria-live="polite"
        >
          <div
            className="relative flex items-center gap-[1.2vw] h-[clamp(96px,9vw,150px)] px-[1.5vw] backdrop-blur-[16px] border-t border-white/10"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 100%)' }}
          >
            {cartoes.length > 1 && (
              <StoriesProgressBar total={cartoes.length} currentIdx={idx} progress={progress} />
            )}

            {dados.capa_url && capaOk && (
              <img
                src={dados.capa_url}
                alt=""
                onError={() => setCapaOk(false)}
                className="h-[70%] aspect-square object-cover rounded-lg ring-1 ring-white/20 shadow-[0_4px_16px_rgba(0,0,0,0.55)] shrink-0"
              />
            )}

            <div className="min-w-0 flex-1">
              <p
                className="text-orange-400 font-black uppercase tracking-[0.2em] leading-none mb-[0.5vw] text-[clamp(8px,0.58vw,12px)]"
                style={{ textShadow: TEXT_SHADOW }}
              >
                Raio-X da música
              </p>

              <motion.div
                key={cartao.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <p
                  className="text-white font-bold leading-tight line-clamp-1 text-[clamp(16px,1.6vw,34px)]"
                  style={{ textShadow: TEXT_SHADOW }}
                >
                  {cartao.principal}
                </p>
                {cartao.secundario && (
                  <p
                    className="text-white/60 font-medium leading-tight line-clamp-1 mt-[0.3vw] text-[clamp(11px,1.05vw,22px)]"
                    style={{ textShadow: TEXT_SHADOW }}
                  >
                    {cartao.secundario}
                  </p>
                )}
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
